import React from 'react';
import {
  Button,
  ButtonProps,
  CircularProgress,
  Box
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';

interface LoadingButtonProps extends Omit<ButtonProps, 'children'> {
  loading?: boolean;
  success?: boolean;
  loadingText?: string;
  successText?: string;
  children: React.ReactNode;
  loadingPosition?: 'start' | 'center' | 'end';
}

/**
 * Enhanced Button component with loading and success states
 *
 * Usage:
 * <LoadingButton loading={isSubmitting} loadingText="Saving...">
 *   Save Changes
 * </LoadingButton>
 */
export const LoadingButton: React.FC<LoadingButtonProps> = ({
  loading = false,
  success = false,
  loadingText,
  successText,
  children,
  disabled,
  startIcon,
  endIcon,
  loadingPosition = 'center',
  sx,
  ...props
}) => {
  const isDisabled = disabled || loading;

  const getButtonContent = () => {
    if (success && successText) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckIcon sx={{ fontSize: 20 }} />
          {successText}
        </Box>
      );
    }

    if (loading) {
      const spinner = (
        <CircularProgress
          size={20}
          color="inherit"
          sx={{
            opacity: 0.8
          }}
        />
      );

      if (loadingPosition === 'start') {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {spinner}
            {loadingText || children}
          </Box>
        );
      }

      if (loadingPosition === 'end') {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {loadingText || children}
            {spinner}
          </Box>
        );
      }

      // Center position - replace content
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {spinner}
          {loadingText && <span>{loadingText}</span>}
        </Box>
      );
    }

    return children;
  };

  return (
    <Button
      {...props}
      disabled={isDisabled}
      startIcon={!loading && !success ? startIcon : undefined}
      endIcon={!loading && !success ? endIcon : undefined}
      sx={{
        position: 'relative',
        transition: 'all 0.2s ease-in-out',
        minWidth: loading ? 120 : undefined,
        ...(success && {
          bgcolor: 'success.main',
          '&:hover': {
            bgcolor: 'success.dark'
          }
        }),
        ...sx
      }}
    >
      {getButtonContent()}
    </Button>
  );
};

/**
 * Icon button variant with loading state
 */
interface LoadingIconButtonProps extends Omit<ButtonProps, 'children'> {
  loading?: boolean;
  icon: React.ReactNode;
}

export const LoadingIconButton: React.FC<LoadingIconButtonProps> = ({
  loading = false,
  icon,
  disabled,
  sx,
  ...props
}) => {
  return (
    <Button
      {...props}
      disabled={disabled || loading}
      sx={{
        minWidth: 'auto',
        p: 1,
        ...sx
      }}
    >
      {loading ? (
        <CircularProgress size={24} color="inherit" />
      ) : (
        icon
      )}
    </Button>
  );
};

export default LoadingButton;
